import { Image as ExpoImage } from 'expo-image';
import { Link, Redirect, router, useLocalSearchParams } from 'expo-router';
import { Eye, EyeOff } from 'lucide-react-native';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Pressable, StyleSheet, Text, View } from 'react-native';

import { Button } from '@/src/components/Button';
import { DateOfBirthPicker } from '@/src/components/DateOfBirthPicker';
import { FacebookButton, GoogleButton } from '@/src/components/FacebookButton';
import { Input } from '@/src/components/Input';
import { PublicPageShell } from '@/src/components/PublicPageShell';
import { SessionLoadingView } from '@/src/components/SessionLoadingView';
import { useToast } from '@/src/components/Toast';
import {
  getGoogleSignUpPrefillProfile,
  isFacebookSignUpRequiredError,
  isGoogleSignUpRequiredError,
} from '@/src/features/auth/auth-service';
import {
  signUpSchema,
  type SignUpValues,
} from '@/src/features/auth/validation';
import { useFacebookAuthRequest } from '@/src/features/auth/use-facebook-auth';
import type { FacebookPrefillProfile } from '@/src/features/auth/use-facebook-auth';
import { useGoogleAuthRequest } from '@/src/features/auth/use-google-auth';
import type { GooglePrefillProfile } from '@/src/features/auth/use-google-auth';
import { useAuth } from '@/src/features/auth/use-auth';
import {
  resolveInviteCode,
  type ResolvedInviteProfile,
} from '@/src/features/invites/invite-service';
import { useAppStore } from '@/src/state/app-store';
import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';

function InviteCard({
  inviter,
  loading,
}: {
  inviter: ResolvedInviteProfile | null;
  loading: boolean;
}) {
  if (loading) {
    return (
      <View style={styles.inviteCard}>
        <Text style={styles.inviteEyebrow}>Invite</Text>
        <Text style={styles.inviteTitle}>Checking your invite...</Text>
      </View>
    );
  }

  if (!inviter) {
    return null;
  }

  const fullName = inviter.fullName ?? 'A Frienzzi friend';
  const initial = fullName.slice(0, 1).toUpperCase();
  const reactionTotal = inviter.stats.totalLikeCount;

  return (
    <View style={styles.inviteCard}>
      <Text style={styles.inviteEyebrow}>Invite</Text>
      <View style={styles.inviteIdentityRow}>
        <View style={styles.inviteAvatar}>
          {inviter.photoURL ? (
            <ExpoImage
              accessibilityLabel=""
              cachePolicy="memory-disk"
              contentFit="cover"
              source={{ uri: inviter.photoURL }}
              style={styles.inviteAvatarImage}
            />
          ) : (
            <Text style={styles.inviteAvatarText}>{initial}</Text>
          )}
        </View>
        <View style={styles.inviteIdentityText}>
          <Text style={styles.inviteTitle}>{fullName} invited you</Text>
          {inviter.username ? (
            <Text style={styles.inviteUsername}>@{inviter.username}</Text>
          ) : null}
        </View>
      </View>
      <View style={styles.inviteStatsRow}>
        <View style={styles.inviteStat}>
          <Text style={styles.inviteStatValue}>
            {inviter.stats.followerCount}
          </Text>
          <Text style={styles.inviteStatLabel}>Followers</Text>
        </View>
        <View style={styles.inviteStat}>
          <Text style={styles.inviteStatValue}>
            {inviter.stats.followingCount}
          </Text>
          <Text style={styles.inviteStatLabel}>Following</Text>
        </View>
        <View style={styles.inviteStat}>
          <Text style={styles.inviteStatValue}>{reactionTotal}</Text>
          <Text style={styles.inviteStatLabel}>Reactions</Text>
        </View>
        <View style={styles.inviteStat}>
          <Text style={styles.inviteStatValue}>
            {inviter.stats.inviteCount}
          </Text>
          <Text style={styles.inviteStatLabel}>Invites</Text>
        </View>
      </View>
    </View>
  );
}

export default function SignUpScreen() {
  const params = useLocalSearchParams<{ invite?: string | string[] }>();
  const {
    initializing,
    signInWithFacebook,
    signInWithGoogle,
    signUpWithFacebook,
    signUpWithGoogle,
    signUp,
    user,
  } = useAuth();
  const { showError, showSuccess } = useToast();
  const facebookAuth = useFacebookAuthRequest();
  const googleAuth = useGoogleAuthRequest();
  const appStoreHydrated = useAppStore((state) => state.hydrated);
  const pendingInvite = useAppStore((state) => state.pendingInvite);
  const setPendingInvite = useAppStore((state) => state.setPendingInvite);
  const setPendingEmailVerificationRegistration = useAppStore(
    (state) => state.setPendingEmailVerificationRegistration,
  );
  const setPendingOnboardingFacebookPhoto = useAppStore(
    (state) => state.setPendingOnboardingFacebookPhoto,
  );
  const queryInviteCode = useMemo(() => {
    const value = params.invite;

    return Array.isArray(value) ? value[0] : value;
  }, [params.invite]);
  const inviteCodeToResolve = pendingInvite?.code ?? queryInviteCode ?? null;
  const [resolvedInvite, setResolvedInvite] =
    useState<ResolvedInviteProfile | null>(null);
  const [resolvingInvite, setResolvingInvite] = useState(false);
  const [facebookPrefill, setFacebookPrefill] =
    useState<FacebookPrefillProfile | null>(null);
  const [googlePrefill, setGooglePrefill] =
    useState<GooglePrefillProfile | null>(null);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [prefillingFacebook, setPrefillingFacebook] = useState(false);
  const [prefillingGoogle, setPrefillingGoogle] = useState(false);
  const {
    control,
    formState: { errors, isSubmitting },
    getValues,
    handleSubmit,
    setError,
    setValue,
  } = useForm<SignUpValues>({
    defaultValues: {
      acceptedLegal: false,
      dateOfBirth: '',
      email: '',
      fullName: '',
      password: '',
    },
  });

  useEffect(() => {
    if (user) {
      setResolvedInvite(null);
      setResolvingInvite(false);

      if (pendingInvite) {
        setPendingInvite(null);
      }
      return undefined;
    }

    if (!appStoreHydrated) {
      return undefined;
    }

    if (!inviteCodeToResolve) {
      setResolvedInvite(null);
      setResolvingInvite(false);
      return undefined;
    }

    let active = true;
    setResolvingInvite(true);

    void resolveInviteCode(inviteCodeToResolve)
      .then((result) => {
        if (!active) {
          return;
        }

        if (result.valid && result.inviter) {
          setResolvedInvite(result.inviter);

          if (!pendingInvite) {
            setPendingInvite({ code: inviteCodeToResolve });
          }
          return;
        }

        setResolvedInvite(null);

        if (pendingInvite?.code === inviteCodeToResolve) {
          setPendingInvite(null);
        }
      })
      .catch(() => {
        if (active) {
          setResolvedInvite(null);
        }
      })
      .finally(() => {
        if (active) {
          setResolvingInvite(false);
        }
      });

    return () => {
      active = false;
    };
  }, [
    appStoreHydrated,
    inviteCodeToResolve,
    pendingInvite,
    pendingInvite?.code,
    setPendingInvite,
    user,
  ]);

  if (initializing) {
    return <SessionLoadingView allowRecovery={false} />;
  }

  if (user) {
    return <Redirect href="/home" />;
  }

  function parseSignUpForm(values: SignUpValues) {
    const parsed = signUpSchema.safeParse(values);

    if (!parsed.success) {
      parsed.error.issues.forEach((issue) => {
        const field = issue.path[0];

        if (
          field === 'dateOfBirth' ||
          field === 'email' ||
          field === 'fullName' ||
          field === 'password' ||
          field === 'acceptedLegal'
        ) {
          setError(field, { message: issue.message });
        }
      });
      return null;
    }

    setValue('dateOfBirth', parsed.data.dateOfBirth);
    return parsed.data;
  }

  const onSubmit = handleSubmit(async (values) => {
    const parsed = parseSignUpForm(values);

    if (!parsed) {
      return;
    }

    try {
      const inviteCode = pendingInvite?.code ?? null;

      if (googlePrefill) {
        const nextUser = await signUpWithGoogle(
          googlePrefill,
          parsed.email,
          parsed.password,
          parsed.acceptedLegal,
          parsed.fullName,
          parsed.dateOfBirth,
          inviteCode,
        );
        setPendingEmailVerificationRegistration({
          authMethod: 'google',
          dateOfBirth: parsed.dateOfBirth,
          email: parsed.email,
          fullName: parsed.fullName,
          inviteCode,
          providerPhotoURL: googlePrefill.photoURL ?? null,
          uid: nextUser.uid,
        });
        setPendingOnboardingFacebookPhoto(
          googlePrefill.photoURL
            ? { uid: nextUser.uid, url: googlePrefill.photoURL }
            : null,
        );
        showSuccess('Check your email to activate Frienzzi.', 'Email sent');
      } else if (facebookPrefill) {
        const nextUser = await signUpWithFacebook(
          facebookPrefill.accessToken,
          parsed.email,
          parsed.password,
          parsed.acceptedLegal,
          parsed.fullName,
          parsed.dateOfBirth,
          inviteCode,
        );
        setPendingEmailVerificationRegistration({
          authMethod: 'facebook',
          dateOfBirth: parsed.dateOfBirth,
          email: parsed.email,
          fullName: parsed.fullName,
          inviteCode,
          providerPhotoURL: facebookPrefill.photoURL ?? null,
          uid: nextUser.uid,
        });
        setPendingOnboardingFacebookPhoto(
          facebookPrefill.photoURL
            ? { uid: nextUser.uid, url: facebookPrefill.photoURL }
            : null,
        );
        showSuccess('Check your email to activate Frienzzi.', 'Email sent');
      } else {
        setPendingOnboardingFacebookPhoto(null);
        const nextUser = await signUp(
          parsed.email,
          parsed.password,
          parsed.acceptedLegal,
          parsed.fullName,
          parsed.dateOfBirth,
          inviteCode,
        );
        setPendingEmailVerificationRegistration({
          authMethod: 'password',
          dateOfBirth: parsed.dateOfBirth,
          email: parsed.email,
          fullName: parsed.fullName,
          inviteCode,
          providerPhotoURL: null,
          uid: nextUser.uid,
        });
        showSuccess('Check your email to activate Frienzzi.', 'Email sent');
      }
      setPendingInvite(null);
      router.replace('/verify-email');
    } catch (error) {
      showError(error, 'Unable to create your account. Please try again.');
    }
  });

  async function handleFacebookPrefill() {
    setPrefillingFacebook(true);
    try {
      const accessToken = await facebookAuth.promptForAccessToken();

      try {
        await signInWithFacebook(accessToken);
        setPendingOnboardingFacebookPhoto(null);
        setPendingInvite(null);
        showSuccess(
          'That Facebook account is already connected, so we signed you in.',
          'Signed in with Facebook',
        );
        router.replace('/home');
        return;
      } catch (error) {
        if (!isFacebookSignUpRequiredError(error)) {
          throw error;
        }
      }

      const profile = await facebookAuth.fetchPrefillProfile(accessToken);
      const currentValues = getValues();

      setFacebookPrefill(profile);
      setGooglePrefill(null);

      if (profile.fullName && !currentValues.fullName.trim()) {
        setValue('fullName', profile.fullName, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }

      if (profile.email && !currentValues.email.trim()) {
        setValue('email', profile.email, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }

      showSuccess(
        'Review the form, add anything missing, then create your account.',
        'Facebook info added',
      );
    } catch (error) {
      showError(error, 'Unable to use Facebook to fill signup details.');
    } finally {
      setPrefillingFacebook(false);
    }
  }

  async function handleGooglePrefill() {
    setPrefillingGoogle(true);
    try {
      const tokens = await googleAuth.promptForCredentialTokens();

      try {
        await signInWithGoogle(tokens);
        setPendingOnboardingFacebookPhoto(null);
        setPendingInvite(null);
        showSuccess(
          'That Google account is already connected, so we signed you in.',
          'Signed in with Google',
        );
        router.replace('/home');
        return;
      } catch (error) {
        if (!isGoogleSignUpRequiredError(error)) {
          throw error;
        }

        const profile =
          getGoogleSignUpPrefillProfile(error) ??
          (tokens ? await googleAuth.fetchPrefillProfile(tokens) : null);

        if (!profile) {
          throw error;
        }

        const currentValues = getValues();

        setGooglePrefill(profile);
        setFacebookPrefill(null);

        if (profile.fullName && !currentValues.fullName.trim()) {
          setValue('fullName', profile.fullName, {
            shouldDirty: true,
            shouldValidate: true,
          });
        }

        if (profile.email && !currentValues.email.trim()) {
          setValue('email', profile.email, {
            shouldDirty: true,
            shouldValidate: true,
          });
        }

        showSuccess(
          'Review the form, add anything missing, then create your account.',
          'Google info added',
        );
      }
    } catch (error) {
      showError(error, 'Unable to use Google to fill signup details.');
    } finally {
      setPrefillingGoogle(false);
    }
  }

  return (
    <PublicPageShell
      title="Join frienzzi"
      subtitle="Create your account."
      maxWidth={560}
    >
      <View style={styles.form}>
        <InviteCard inviter={resolvedInvite} loading={resolvingInvite} />
        <Controller
          control={control}
          name="fullName"
          render={({ field: { onBlur, onChange, value } }) => (
            <Input
              label="Full name"
              autoCapitalize="words"
              onBlur={onBlur}
              onChangeText={onChange}
              textContentType="name"
              value={value}
              error={errors.fullName?.message}
            />
          )}
        />
        <Controller
          control={control}
          name="dateOfBirth"
          render={({ field: { onBlur, onChange, value } }) => (
            <DateOfBirthPicker
              label="Date of birth"
              onBlur={onBlur}
              onChangeText={onChange}
              value={value}
              error={errors.dateOfBirth?.message}
            />
          )}
        />
        <Controller
          control={control}
          name="email"
          render={({ field: { onBlur, onChange, value } }) => (
            <Input
              label="Email"
              autoComplete="email"
              keyboardType="email-address"
              onBlur={onBlur}
              onChangeText={onChange}
              textContentType="emailAddress"
              value={value}
              error={errors.email?.message}
            />
          )}
        />
        <Controller
          control={control}
          name="password"
          render={({ field: { onBlur, onChange, value } }) => (
            <Input
              label="Password"
              autoComplete="new-password"
              onBlur={onBlur}
              onChangeText={onChange}
              passwordRules="minlength: 8;"
              secureTextEntry={!passwordVisible}
              textContentType="newPassword"
              value={value}
              error={errors.password?.message}
              rightAccessory={
                <Pressable
                  accessibilityLabel={
                    passwordVisible ? 'Hide password' : 'Show password'
                  }
                  accessibilityRole="button"
                  onPress={() => setPasswordVisible((visible) => !visible)}
                  style={styles.passwordToggle}
                >
                  {passwordVisible ? (
                    <EyeOff
                      color={colors.secondary}
                      size={20}
                      strokeWidth={2.4}
                    />
                  ) : (
                    <Eye color={colors.secondary} size={20} strokeWidth={2.4} />
                  )}
                </Pressable>
              }
            />
          )}
        />
        <Controller
          control={control}
          name="acceptedLegal"
          render={({ field: { onChange, value } }) => (
            <View style={styles.legalField}>
              <Pressable
                accessibilityLabel="Accept Terms of Service and Privacy Policy"
                accessibilityRole="checkbox"
                accessibilityState={{ checked: value }}
                onPress={() => onChange(!value)}
                style={styles.checkboxRow}
              >
                <View style={[styles.checkbox, value ? styles.checked : null]}>
                  {value ? <Text style={styles.checkmark}>✓</Text> : null}
                </View>
                <Text style={styles.legalText}>
                  I agree to the{' '}
                  <Link href="/terms" style={styles.link}>
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link href="/privacy" style={styles.link}>
                    Privacy Policy
                  </Link>
                  .
                </Text>
              </Pressable>
              {errors.acceptedLegal?.message ? (
                <Text style={styles.error}>{errors.acceptedLegal.message}</Text>
              ) : null}
            </View>
          )}
        />
        <Button
          title="Create account"
          loading={isSubmitting}
          onPress={onSubmit}
        />
        <View style={styles.divider}>
          <View style={styles.dividerLine} />
          <Text style={styles.dividerText}>or</Text>
          <View style={styles.dividerLine} />
        </View>
        {googleAuth.available ? (
          <GoogleButton
            title={
              googlePrefill ? 'Google info added' : 'Use Google to fill this in'
            }
            disabled={googleAuth.disabled || isSubmitting}
            loading={prefillingGoogle}
            onPress={handleGooglePrefill}
          />
        ) : null}
        <FacebookButton
          title={
            facebookPrefill
              ? 'Facebook info added'
              : 'Use Facebook to fill this in'
          }
          disabled={facebookAuth.disabled || isSubmitting}
          loading={prefillingFacebook}
          onPress={handleFacebookPrefill}
        />
        {googlePrefill || facebookPrefill ? (
          <Text style={styles.providerHelp}>
            {googlePrefill ? 'Google' : 'Facebook'} filled in what it could. You
            still need to review these details and create an email and password
            account.
          </Text>
        ) : null}
      </View>
      <Text style={styles.footer}>
        Already have an account?{' '}
        <Link href="/sign-in" style={styles.link}>
          Sign in
        </Link>
      </Text>
    </PublicPageShell>
  );
}

const styles = StyleSheet.create({
  form: {
    gap: spacing.lg,
  },
  inviteCard: {
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.md,
    padding: spacing.lg,
  },
  inviteEyebrow: {
    color: colors.primary,
    fontSize: 12,
    fontWeight: '900',
    textTransform: 'uppercase',
  },
  inviteIdentityRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.md,
  },
  inviteAvatar: {
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 28,
    borderWidth: 1,
    height: 56,
    justifyContent: 'center',
    overflow: 'hidden',
    width: 56,
  },
  inviteAvatarImage: {
    height: '100%',
    width: '100%',
  },
  inviteAvatarText: {
    color: colors.primary,
    fontSize: 20,
    fontWeight: '900',
  },
  inviteIdentityText: {
    flex: 1,
    gap: 2,
    minWidth: 0,
  },
  inviteTitle: {
    color: colors.text,
    fontSize: 17,
    fontWeight: '900',
  },
  inviteUsername: {
    color: colors.mutedText,
    fontSize: 14,
    fontWeight: '700',
  },
  inviteStatsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  inviteStat: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    flexBasis: '22%',
    flexGrow: 1,
    minWidth: 92,
    padding: spacing.sm,
  },
  inviteStatValue: {
    color: colors.text,
    fontSize: 18,
    fontWeight: '900',
  },
  inviteStatLabel: {
    color: colors.mutedText,
    fontSize: 12,
    fontWeight: '800',
  },
  divider: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.md,
  },
  dividerLine: {
    backgroundColor: colors.border,
    flex: 1,
    height: StyleSheet.hairlineWidth,
  },
  dividerText: {
    color: colors.mutedText,
    fontSize: 13,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  error: {
    color: colors.danger,
    fontSize: 14,
  },
  legalField: {
    gap: spacing.sm,
  },
  checkboxRow: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    gap: spacing.md,
  },
  checkbox: {
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderColor: colors.secondary,
    borderRadius: 6,
    borderWidth: 2,
    height: 24,
    justifyContent: 'center',
    marginTop: 1,
    width: 24,
  },
  checked: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  checkmark: {
    color: colors.surface,
    fontSize: 15,
    fontWeight: '900',
    lineHeight: 18,
  },
  legalText: {
    color: colors.mutedText,
    flex: 1,
    fontSize: 14,
    lineHeight: 20,
  },
  passwordToggle: {
    alignItems: 'center',
    borderRadius: 999,
    height: 40,
    justifyContent: 'center',
    width: 40,
  },
  providerHelp: {
    color: colors.mutedText,
    fontSize: 13,
    lineHeight: 18,
    textAlign: 'center',
  },
  footer: {
    color: colors.mutedText,
    fontSize: 15,
    textAlign: 'center',
  },
  link: {
    color: colors.primary,
    fontWeight: '700',
  },
});
